import api from '../../common/api';
import session from '../../share/session';
import config from '../../share/config';
import alert from '../../share/alert';
import handler from '../../share/util/apiResultValidator';
import util from '../../share/util/utils';
import logWrapper from '../../share/logWrapper';

export default {
  namespaced: true,

  state: {
    loggedIn: false, //!!session.getToken()
    forceSignIn: false,
    loginParameters: null,
    lastOperatedTime: 0,
    sessionKeepInterval: process.env.VUE_APP_COMMON_SESSION_KEEP_INTERVAL_MIN_TIME,
    codeVerifier: '',
    codeChallenge: '',
  },

  mutations: {
    loginWithAd(state) {
      state.loggedIn = true;
      state.forceSignIn = false;
      session.startSessionTimer();
    },

    login(state) {
      state.loggedIn = true;
      state.forceSignIn = false;
    },

    setForceSignIn(state, payload) {
      state.forceSignIn = payload;
    },

    logout(state) {
      state.loggedIn = false;
      state.forceSignIn = false;
      session.stopSessionTimer();
      session.destroyToken();
      session.destroyLoginToken();
      session.destroyUser();
    },

    setLoginParameters(state, payload) {
      state.loginParameters = payload;
    },

    setLatOperatedTime(state, payload) {
      state.lastOperatedTime = payload;
    },

    setIntervalTime(state, payload) {
      state.sessionKeepInterval = payload;
    },

    setCodeVerifier(state, codeVerifier) {
      state.codeVerifier = codeVerifier;
    },

    setCodeChallenge(state, codeChallenge) {
      state.codeChallenge = codeChallenge;
    }
  },

  actions: {
    setForceSignIn(context, payload) {
      context.commit('setForceSignIn', payload);
    },

    // 外部認証サーバーログイン処理
    async externalLogin(context, payload) {
      // セッションクリア
      session.destroyToken();
      session.destroyLoginToken();
      session.destroyUser();

      /**
       * 外部連携サインイン処理
       * @param {string} code 認証コード
       * @param {string} provider プロバイダー
       * @param {string} code_verifier PKCEで使用するランダムな文字列
       * @param {is_admin} is_admin true:管理画面 false:実行画面
       */
      const param = {
        code: payload.code,
        provider: payload.provider,
        code_verifier: payload.code_verifier, // sessionStorageからcode_verifierを取得
        is_admin: false,
        redirect_url: payload.redirect_url
      };
      try {
        const loginResponse = await api.postCall(config.ADMIN, '/signin/external', param);

        // externalエラー時でもIDPでサインアウトAPIを実行できるようにtokenを取得する
         if (loginResponse.data?.details) {
           session.saveToken(loginResponse.data.details);
           session.saveLoginToken(loginResponse.data.details);
         }

        // APIレスポンスの検証
        const isSuccess = handler.validate(
          handler.validateTypes.response, loginResponse, payload.modal, null, null, null, null, false);

        if (isSuccess) {
          // ログイン成功を設定
          session.saveToken(loginResponse.data);
          session.saveLoginToken(loginResponse.data);
          session.saveUser(loginResponse.data.user_id);
          // ログイン応答内容を保持
          context.commit('setLoginParameters', loginResponse.data);

          // APPにログインしたユーザーの言語情報を設定する。 
          context.dispatch('systems/setLanguage', { language: loginResponse.data.language }, { root: true });
          // SAML連携情報をloginResponseに追加する。
          loginResponse.data["is_external_auth"] = true;
          // ログインユーザー情報を設定する。 
          context.dispatch('userProperty/setUserProperty', loginResponse.data, { root: true });
          // ログインユーザーが利用可能な機能を設定する
          context.dispatch('userProperty/setFunctionAvailability', loginResponse.data.functions, { root: true });
          // ログイン完全の場合、トップ画面の表示
          context.commit("login");

          // Start session expiry timer.
          const interval = convertMinToMilliseconds(loginResponse.data.session_timeout);
          setupSessionTimer(context, payload, interval);
          return { isSuccess: true, response: loginResponse };
        } else {
          return { isSuccess: false, response: loginResponse };
        }
      } catch (error) {
        return { isSuccess: false, response: error };
      }
    },

    async logout(context, payload) {
      try {
        context.dispatch('app/setProcessingIcon', true, { root: true });
        await context.dispatch('userProperty/saveUserCache', {}, { root: true });
        const param = {
          refresh_token: session.getLoginToken().refresh_token,
          is_admin: false // 管理画面:true 実行画面:false
        };
        await api.postCall(config.ADMIN, '/signout', param);
      } catch (error) {
        // console.log('Exception in signout: ', error);
      }

      context.dispatch('app/setProcessingIcon', false, { root: true });

      context.dispatch('app/setInitLogin', false, { root: true });
      context.commit('logout');

      if (payload) {
        // 動作しているタイマーを全て停止する
        payload.$stopAllIntervals();

        // ログイン画面遷移実施情報をチェック
        let moveLogin = true;
        if ('isMoveLogin' in payload) { moveLogin = payload.isMoveLogin; }
        if (moveLogin) {
          // SSM5処理エラーの場合、SUBARUユーザーはIDP画面に遷移し,それ以外のSSO連携ユーザーはログイン画面に遷移する
          if (context.rootState.provider.currentProvider === config.SBR_DOMAIN || context.rootState.provider.currentProvider === config.SBR_TECH_DOMAIN) {
            window.location.href = payload.$store.state.systems.samlAccountList[0].sloLogoutUrl;
          } else {
            await payload.$router.push({ name: 'login', query: { isSkip: true } }).catch(err => { }); // eslint-disable-line no-unused-vars
          }
        }
      }
      // セッションタイムアウトによるログアウトの場合、ログアウト時にstoreの初期化を実施するとTypeErrorが起きるため、警告モーダルOKボタン押下時（setupSessionTimer内）に初期化を実施する
      if (!util.hasPropertyCheck(payload, 'isSessionTimeout')) {
        context.dispatch('reset', {}, { root: true });
      }

      // session メンバ変数を初期化
      session.terminateSession();

    },

    /**
     * ページ離脱（ブラウザ閉じる）時のログアウト処理
     * @param {*} context context
     */
    logoutForPageLeave(context) {
      try {
        const token = session.getToken();
        const loginToken = session.getLoginToken();
        const refreshToken = loginToken && loginToken.refresh_token;
        const idToken = token && token.id_token;
        const sessionToken = loginToken && loginToken.session_token;

        if (refreshToken && idToken && typeof window !== 'undefined' && typeof window.fetch === 'function') {
          const endPoint = api.createRequestURL(config.ADMIN, '/signout');
          const baseURL = process.env.VUE_APP_DEV_ENVIRONMENT ? '' : api.createBaseURL();
          const endURL = `${baseURL}${endPoint}`;
          const requestBody = JSON.stringify({
            refresh_token: refreshToken,
            is_admin: false,
          });
          void window.fetch(endURL, {
            method: 'POST',
            keepalive: true,
            headers: {
              Authorization: idToken,
              'Content-Type': 'application/json',
              ...(sessionToken ? { 'x-repro-session-token': sessionToken } : {}),
            },
            body: requestBody,
          });
        }
      } catch (error) {
        error;
      }

      context.dispatch('userProperty/saveUserCache', {}, { root: true });
      context.dispatch('app/setInitLogin', false, { root: true });
      context.commit('logout');
      context.dispatch('reset', {}, { root: true });
      session.terminateSession();
    },

    async resetSession(context, payload) {

      context.commit('logout');

      if (payload) {
        // 動作しているタイマーを全て停止する
        payload.$stopAllIntervals();
        // ログイン画面に遷移
        await payload.$router.push({ name: 'login' }).catch(err => { }); // eslint-disable-line no-unused-vars
      }
      context.dispatch('reset', {}, { root: true });
    },

    stopSessionTimer() {
      // セッションタイマーを停止する
      session.stopSessionTimer();
    },

    resetSessionTimer() {
      // タイムアウト上限の算出
      const sessionTimeout = util.getSessionLimit({});
      // セッションタイムアウトを設定
      session.setSessionTimeOut(convertMinToMilliseconds(sessionTimeout));
      session.startSessionTimer();
    },

    updateLastOperationTime(context, payload) {
      context.commit('setLatOperatedTime', payload);
    },

    // code_verifer生成(43文字)
    generateCodeVerifier(context) {
      let codeVerifier = "";
      const codeLength = 43;
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
      const array = new Uint32Array(codeLength);
      window.crypto.getRandomValues(array);
      codeVerifier = Array.from(array, dec => characters[dec % characters.length]).join('');
      context.commit('setCodeVerifier', codeVerifier);
      // sessionストレージにcode_verifierを保存
      session.setCodeVerifier(codeVerifier);
      return codeVerifier;
    },

    // code_challenge生成(code_challenge_method=S256)
    async generateCodeChallenge(context) {
      const codeVerifier = context.state.codeVerifier;
      const codeBuffer = new TextEncoder().encode(codeVerifier);
      const hashBuffer = await crypto.subtle.digest('SHA-256', codeBuffer);
      const hashString = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_');
      context.commit('setCodeChallenge', hashString);
      return hashString;
    }
  }
};

const setupSessionTimer = (context, payload, millisec) => {
  session.stopSessionTimer();
  // タイムアウト時間の取得
  const defaultMin = util.getSessionLimit(context.state.loginParameters);
  const sessionTime = convertMinToMilliseconds(defaultMin);
  // Session timeout 抑制インターバル間隔時間を設定
  let intervalTime = sessionTime / 2;
  if (intervalTime < process.env.VUE_APP_COMMON_SESSION_KEEP_INTERVAL_MIN_TIME) {
    intervalTime = process.env.VUE_APP_COMMON_SESSION_KEEP_INTERVAL_MIN_TIME;
  }
  context.commit("setIntervalTime", intervalTime);
  // セッションタイムアウトの設定
  logWrapper.log("Session Timer has been setup: ", millisec);
  session.setSessionTimerConfig(async () => {
    logWrapper.log("Operation timeout loggedIn: ", context.state.loggedIn);
    if (context.state.loggedIn) {
      // 最終マウス操作時間が記録されている場合、現在時間との差分を計算
      const lastOperation = context.state.lastOperatedTime;
      const wastedTime = lastOperation > 0 ? (new Date().getTime()) - lastOperation : 0;
      // 差分時間がデフォルトのタイムアウト時間未満なら、その差分だけ再度タイマーセット
      if (lastOperation > 0 && wastedTime < sessionTime) {
        // 任意の API にアクセスして API のセッションを更新
        updateApiSession(context);
        // Frontend 操作タイムアウト時間を更新
        const nextTimeoutInterval = sessionTime - wastedTime;
        logWrapper.log("Next Timeout Interval: ", nextTimeoutInterval);
        setupSessionTimer(context, payload, nextTimeoutInterval);
      } else {
        // auth.logout 関数内では Login 画面に移動させない
        const arg = payload.modal;
        arg.isMoveLogin = false;
        arg.isSessionTimeout = null;
        context.dispatch('auth/logout', arg, { root: true });
        // 操作タイムアウトダイアログのメッセージ設定
        const formattedMsg = payload.modal.$t('message.sesion_timeout_message').replace('####', defaultMin);
        const title = payload.modal.$t('message.sesion_timeout_title');
        logWrapper.log("Operation timeout showWarning");
        alert.showWarning(payload.modal, title, formattedMsg, async () => {
          await payload.modal.$router.push({ name: 'login', query: { isSkip: false } }).catch(err => { err; });
          context.dispatch('reset', {}, { root: true });
        });
      }
    }
  }, millisec); // Timout in milliseconds
  // session監視タイマー開始
  session.startSessionTimer();
};

// API アクセスセッションを延長するための API アクセス
const updateApiSession = (context) => {
  try {
    api.getCall(config.ADMIN, '/users/' + context.state.loginParameters.user_id + '/caches');
  } catch (err) {
    logWrapper.log("Failed to API access for updating session token. User: ", context.state.loginParameters.user_id);
  }
};

const convertMinToMilliseconds = (timeout) => {
  return timeout * 60000;
};